/**
 * Typed History API router for the Texas 42 web app.
 * Route types live here per Phase 4 spec.
 */

export type RouteName =
  | 'home'
  | 'new-game'
  | 'game'
  | 'game-summary'
  | 'saved-games'
  | 'replay'
  | 'portable-replay'
  | 'stats'
  | 'how-to-play'
  | 'settings'
  | 'privacy'
  | 'expert-inbox'
  | 'gallery'
  | 'not-found';

export type RouteParams = {
  home: Record<string, never>;
  'new-game': Record<string, never>;
  game: { matchId: string };
  'game-summary': { matchId: string };
  'saved-games': Record<string, never>;
  replay: { matchId: string };
  'portable-replay': Record<string, never>;
  stats: Record<string, never>;
  'how-to-play': Record<string, never>;
  settings: Record<string, never>;
  privacy: Record<string, never>;
  'expert-inbox': Record<string, never>;
  gallery: Record<string, never>;
  'not-found': Record<string, never>;
};

export interface RouteLocation<T extends RouteName = RouteName> {
  name: T;
  params: RouteParams[T];
  path: string;
  query: Record<string, string>;
}

export interface FlashMessage {
  title: string;
  description?: string;
  variant?: 'default' | 'success' | 'warning' | 'destructive';
}

export interface NavigateOptions {
  replace?: boolean;
  flash?: FlashMessage;
}

export interface Router {
  readonly location: RouteLocation;
  consumeFlash(): FlashMessage | null;
  push(name: RouteName, params?: RouteParams[RouteName], options?: Omit<NavigateOptions, 'replace'>): void;
  replace(name: RouteName, params?: RouteParams[RouteName], options?: Omit<NavigateOptions, 'replace'>): void;
  navigate(path: string, options?: NavigateOptions): void;
  href(name: RouteName, params?: RouteParams[RouteName]): string;
  subscribe(listener: (location: RouteLocation) => void): () => void;
}

export function getBasePath(): string {
  const base = import.meta.env.BASE_URL ?? '/';
  if (base === '/') return '';
  return base.endsWith('/') ? base.slice(0, -1) : base;
}

export function parseQuery(search: string): Record<string, string> {
  const query: Record<string, string> = {};
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  params.forEach((value, key) => {
    query[key] = value;
  });
  return query;
}

export function stripBasePath(pathname: string, base: string): string {
  if (!base) return pathname || '/';
  if (pathname === base) return '/';
  if (pathname.startsWith(`${base}/`)) {
    return pathname.slice(base.length) || '/';
  }
  return pathname || '/';
}

type Matcher = {
  name: RouteName;
  pattern: RegExp;
  params: (match: RegExpMatchArray) => RouteParams[RouteName];
};

const MATCHERS: Matcher[] = [
  { name: 'expert-inbox', pattern: /^\/expert-inbox\/?$/, params: () => ({}) },
  {
    name: 'game-summary',
    pattern: /^\/game\/([^/]+)\/summary\/?$/,
    params: (match) => ({ matchId: decodeURIComponent(match[1]!) }),
  },
  {
    name: 'game',
    pattern: /^\/game\/([^/]+)\/?$/,
    params: (match) => ({ matchId: decodeURIComponent(match[1]!) }),
  },
  {
    name: 'replay',
    pattern: /^\/replay\/([^/]+)\/?$/,
    params: (match) => ({ matchId: decodeURIComponent(match[1]!) }),
  },
  {
    name: 'portable-replay',
    pattern: /^\/replay\/?$/,
    params: () => ({}),
  },
  {
    name: 'new-game',
    pattern: /^\/new-game\/?$/,
    params: () => ({}),
  },
  {
    name: 'saved-games',
    pattern: /^\/saved-games\/?$/,
    params: () => ({}),
  },
  {
    name: 'stats',
    pattern: /^\/stats\/?$/,
    params: () => ({}),
  },
  {
    name: 'how-to-play',
    pattern: /^\/how-to-play\/?$/,
    params: () => ({}),
  },
  {
    name: 'privacy',
    pattern: /^\/privacy\/?$/,
    params: () => ({}),
  },
  {
    name: 'settings',
    pattern: /^\/settings\/?$/,
    params: () => ({}),
  },
  {
    name: 'gallery',
    pattern: /^\/gallery\/?$/,
    params: () => ({}),
  },
  {
    name: 'home',
    pattern: /^\/$/,
    params: () => ({}),
  },
];

export function matchPath(pathname: string): RouteLocation {
  const normalized = pathname.replace(/\/+$/, '') || '/';

  for (const matcher of MATCHERS) {
    const result = normalized.match(matcher.pattern);
    if (result) {
      return {
        name: matcher.name,
        params: matcher.params(result) as RouteParams[typeof matcher.name],
        path: normalized,
        query: {},
      };
    }
  }

  return {
    name: 'not-found',
    params: {},
    path: normalized,
    query: {},
  };
}

export function parseLocation(pathnameWithSearch: string, base = getBasePath()): RouteLocation {
  const [pathname = '/', search = ''] = pathnameWithSearch.split('?');
  const location = matchPath(stripBasePath(pathname, base));
  location.query = parseQuery(search ? `?${search}` : '');
  return location;
}

export function buildPath(name: RouteName, params: RouteParams[RouteName] = {} as RouteParams[RouteName]): string {
  switch (name) {
    case 'home':
      return '/';
    case 'new-game':
      return '/new-game';
    case 'saved-games':
      return '/saved-games';
    case 'stats':
      return '/stats';
    case 'how-to-play':
      return '/how-to-play';
    case 'settings':
      return '/settings';
    case 'privacy':
      return '/privacy';
    case 'expert-inbox': return '/expert-inbox';
    case 'gallery':
      return '/gallery';
    case 'game':
      return `/game/${encodeURIComponent((params as RouteParams['game']).matchId)}`;
    case 'game-summary':
      return `/game/${encodeURIComponent((params as RouteParams['game-summary']).matchId)}/summary`;
    case 'replay':
      return `/replay/${encodeURIComponent((params as RouteParams['replay']).matchId)}`;
    case 'portable-replay':
      return '/replay';
    case 'not-found':
      return '/';
    default: {
      const _exhaustive: never = name;
      return _exhaustive;
    }
  }
}

export function resolveHref(
  base: string,
  name: RouteName,
  params?: RouteParams[RouteName],
): string {
  const path = buildPath(name, params ?? ({} as RouteParams[RouteName]));
  return `${base}${path}`;
}

export function createRouter(options?: { base?: string; initialPath?: string }): Router {
  const base = options?.base ?? getBasePath();
  let location =
    typeof window !== 'undefined'
      ? parseLocation(
          options?.initialPath ?? `${window.location.pathname}${window.location.search}`,
          base,
        )
      : parseLocation('/', base);
  let pendingFlash: FlashMessage | null = null;
  const listeners = new Set<(next: RouteLocation) => void>();

  function notify() {
    for (const listener of listeners) {
      listener(location);
    }
  }

  function applyUrl(path: string, replace: boolean) {
    const fullPath = path.startsWith(base) ? path : `${base}${path.startsWith('/') ? path : `/${path}`}`;
    if (typeof globalThis.history !== 'undefined') {
      if (replace) {
        globalThis.history.replaceState(null, '', fullPath);
      } else {
        globalThis.history.pushState(null, '', fullPath);
      }
    }
    location = parseLocation(fullPath, base);
    notify();
  }

  const router: Router = {
    get location() {
      return location;
    },
    consumeFlash() {
      const flash = pendingFlash;
      pendingFlash = null;
      return flash;
    },
    push(name, params, options) {
      if (options?.flash) pendingFlash = options.flash;
      applyUrl(buildPath(name, params ?? ({} as RouteParams[typeof name])), false);
    },
    replace(name, params, options) {
      if (options?.flash) pendingFlash = options.flash;
      applyUrl(buildPath(name, params ?? ({} as RouteParams[typeof name])), true);
    },
    navigate(path, options) {
      if (options?.flash) pendingFlash = options.flash;
      applyUrl(path, options?.replace ?? false);
    },
    href(name, params) {
      return resolveHref(base, name, params);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('popstate', () => {
      location = parseLocation(`${window.location.pathname}${window.location.search}`, base);
      notify();
    });
  }

  return router;
}
